import axios from 'axios';

// 1. INTERFACES (Adicionado Sale conforme guia)
export interface Sale {
  id: string;
  sale_number: string;
  customer_name: string;
  customer_phone?: string;
  customer_cpf?: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  cost_price: number;
  amount_paid?: number;
  payment_method?: string;
  notes?: string;
  status: 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
}

const isDev = import.meta.env.DEV;

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://jl-back-system-production.up.railway.app',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // ✅ ISSO PERMITE ENVIAR COOKIES AUTOMÁTICAMENTE
});

// Interceptor de REQUEST - Cookies são enviados automaticamente
api.interceptors.request.use(
  (config) => {
    if (isDev) {
      console.log(`📤 [REQUEST] ${config.method?.toUpperCase()} ${config.url}`);
      console.log(`🍪 withCredentials: ${config.withCredentials}`);
    }
    return config;
  },
  (error) => {
    console.error('❌ [Request Error]', error);
    return Promise.reject(error);
  }
);

// Interceptor de RESPONSE - Trata erros
api.interceptors.response.use(
  (response) => {
    console.log(`✅ [RESPONSE] ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('❌ [Response Error]', {
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.response?.data?.message,
      errorMsg: error.message,
    });
    
    if (error.response?.status === 401) {
      const reqUrl = String(error.config?.url ?? '');
      const isLoginAttempt = reqUrl.includes('/auth/login');
      
      // Se é uma tentativa de login: apenas rejeitar (não redirecionar)
      if (isLoginAttempt) {
        console.error('❌ Login falhou');
        return Promise.reject(error);
      }
      
      // Se é outro erro 401: sessão expirada - redirecionar
      console.warn('⏰ Sessão expirada');
      localStorage.removeItem('user');
      localStorage.removeItem('isAdminAuthenticated');
      localStorage.removeItem('adminAuth');
      
      // Usar replace para evitar voltar para página anterior
      if (typeof window !== 'undefined' && window.location.pathname !== '/admin-login') {
        window.location.href = '/admin-login';
      }
    }

    return Promise.reject(error);
  }
);

export const auth = {
  login: (data: { email: string; password: string }) => 
    api.post('/api/auth/login', data),
  logout: () => api.post('/api/auth/logout'),
  me: () => api.get('/api/auth/me'),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.put('/api/auth/change-password', data),
  forgotPassword: (data: { email: string }) =>
    api.post('/api/auth/forgot-password', data),
  resetPassword: (data: { token: string; newPassword: string }) =>
    api.post('/api/auth/reset-password', data),
  validateResetToken: (data: { token: string }) =>
    api.post('/api/auth/validate-reset-token', data),
};

export const customers = {
  create: (data: any) => api.post('/api/customers', data),
  getAll: (params?: any) => api.get('/api/customers', { params }),
  getById: (id: string) => api.get(`/api/customers/${id}`),
  update: (id: string, data: any) => api.put(`/api/customers/${id}`, data),
  delete: (id: string) => api.delete(`/api/customers/${id}`),
};

export const serviceOrders = {
  create: (data: any) => api.post('/api/service-orders', data),
  getAll: (params?: any) => api.get('/api/service-orders', { params }),
  // Rota dedicada para admin — retorna TODAS as OS sem limite de paginação
  getAllAdmin: (params?: { status?: string; search?: string }) =>
    api.get('/api/service-orders/all', { params }),
  getById: (id: string) => api.get(`/api/service-orders/${id}`),
  update: (id: string, data: any) => api.put(`/api/service-orders/${id}`, data),
  delete: (id: string) => api.delete(`/api/service-orders/${id}`),
};

export const users = {
  create: (data: { name: string; email: string; password: string; role: 'admin' | 'technician' | 'atendente' }) =>
    api.post('/api/users', data),
  getAll: (params?: any) => api.get('/api/users', { params }),
  updateRole: (id: string, role: 'admin' | 'technician' | 'atendente') =>
    api.put(`/api/users/${id}`, { role }),
  delete: (id: string) => api.delete(`/api/users/${id}`),
};

// 2. OBJETO sales (Adicionado conforme guia)
export const sales = {
  getAll: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get<{ sales: Sale[]; total: number; totalPages: number; currentPage: number }>('/api/sales', { params }),

  // Rota dedicada — retorna TODAS as vendas sem limite de paginação
  getAllAdmin: (params?: { status?: string }) =>
    api.get<{ sales: Sale[]; total: number }>('/api/sales/all', { params }),
  
  getById: (id: string) => api.get<Sale>(`/api/sales/${id}`),
  
  create: (data: {
    customer_name: string;
    customer_phone?: string;
    customer_cpf?: string;
    product_name: string;
    quantity: number;
    unit_price: number;
    cost_price?: number;
    amount_paid?: number;
    payment_method?: string;
    notes?: string;
  }) => api.post<Sale>('/api/sales', data),
  
  update: (id: string, data: Partial<Sale>) => api.put<Sale>(`/api/sales/${id}`, data),
  
  cancel: (id: string) => api.delete(`/api/sales/${id}`),
  
  delete: (id: string) => api.delete(`/api/sales/${id}/permanent`),

  getStats: (period?: 'week' | 'month' | 'year') =>
    api.get<{
      totalRevenue: number;
      totalCost: number;
      totalProfit: number;
      totalSales: number;
      averageSale: number;
      period: string;
    }>('/api/sales/stats/summary', { params: { period } }),
};

export const inventory = {
  create: (data: any) => api.post('/api/inventory', data),
  getAll: (params?: any) => api.get('/api/inventory', { params }),
  getById: (id: string) => api.get(`/api/inventory/${id}`),
  update: (id: string, data: any) => api.put(`/api/inventory/${id}`, data),
  delete: (id: string) => api.delete(`/api/inventory/${id}`),
};

export const dashboard = {
  getStats: (params?: any) => api.get('/api/dashboard/stats', { params }),
  getRevenue: (params?: any) => api.get('/api/dashboard/revenue', { params }),
};

export const publicAPI = {
  track: (data: { identifier: string }) => api.post('/api/public/track', data),
};

export default api;
